/**
 * Geolocator Web Component
 *
 * A modular geolocation detection and redirection system for Shopify stores.
 * Uses GeoJS API to detect user location and provides modal for market selection.
 *
 * Features:
 * - Custom web components for easy integration
 * - Cookie-based user preference storage
 * - Auto-redirect functionality
 * - Support for both expansion and market stores
 * - Accessibility compliant
 * - Responsive design
 *
 * @author Grafikr
 * @version 1.0.0
 */

/* eslint-disable */

/* ==========================================================================
  Utility Functions
   ========================================================================== */

/**
 * Cookie management utilities
 */
const CookieManager = {
  /**
   * Set a cookie with specified options
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiration
   * @param {string} path - Cookie path
   * @param {string} domain - Cookie domain
   */
  set(name, value, days = 30, path = '/', domain = null) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

      let cookieString = `${name}=${encodeURIComponent(
        value
      )}; expires=${expires.toUTCString()}; path=${path}`;

      if (domain) {
        cookieString = `${cookieString}; domain=${domain}`;
      }

      document.cookie = cookieString;
      return true;
    } catch (error) {
      console.error('Failed to set cookie:', error);
      return false;
    }
  },

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  get(name) {
    try {
      const nameEQ = `${name}=`;
      const cookies = document.cookie.split(';');

      const matchingCookie = cookies.find((cookie) => {
        const trimmedCookie = cookie.trim();
        return trimmedCookie.indexOf(nameEQ) === 0;
      });

      if (matchingCookie) {
        const trimmedCookie = matchingCookie.trim();
        return decodeURIComponent(trimmedCookie.substring(nameEQ.length));
      }

      return null;
    } catch (error) {
      console.error('Failed to get cookie:', error);
      return null;
    }
  },

  /**
   * Delete a cookie by name
   * @param {string} name - Cookie name
   * @param {string} path - Cookie path
   * @param {string} domain - Cookie domain
   */
  delete(name, path = '/', domain = null) {
    try {
      let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

      if (domain) {
        cookieString = `${cookieString}; domain=${domain}`;
      }

      document.cookie = cookieString;
      return true;
    } catch (error) {
      console.error('Failed to delete cookie:', error);
      return false;
    }
  },
};

/**
 * Geolocation API wrapper
 */
const GeoLocationAPI = {
  /**
   * Fetch user's country from GeoJS API
   * @param {string} endpoint - API endpoint URL
   * @returns {Promise<Object>} Country data
   */
  async fetchCountry(endpoint = 'https://get.geojs.io/v1/ip/country.json') {
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        cache: 'default',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        country: data.country,
        countryCode: data.country_code || data.country,
        continentCode: data.continent_code,
        ip: data.ip,
      };
    } catch (error) {
      console.error('Failed to fetch geolocation:', error);
      return null;
    }
  },
};

/**
 * Modal management utilities
 */
const ModalManager = {
  /**
   * Show modal with animation
   * @param {HTMLElement} modal - Modal element
   */
  show(modal) {
    document.body.style.overflow = 'hidden';
    modal.classList.add('is-visible');

    // Focus management for accessibility
    const firstFocusable = modal.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  },

  /**
   * Hide modal with animation
   * @param {HTMLElement} modal - Modal element
   */
  hide(modal) {
    modal.classList.remove('is-visible');
    document.body.style.overflow = '';

    // Return focus to trigger element if possible
    const trigger = document.querySelector('geolocator-button button');
    if (trigger) {
      trigger.focus();
    }
  },
};

/**
 * URL and redirect utilities
 */
const RedirectManager = {
  /**
   * Check if a URL exists by fetching its JSON representation
   * @param {string} url - URL to check
   * @returns {Promise<boolean>} Whether the URL exists
   */
  async checkUrlExists(url) {
    try {
      const jsonUrl = url.endsWith('/')
        ? `${url.slice(0, -1)}.json`
        : `${url}.json`;

      // Try to fetch with a simple GET request
      const response = await fetch(jsonUrl, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          Accept: 'application/json',
        },
      });

      // Check if response is ok (status 200-299)
      if (response.ok) {
        console.log('🌍 Geolocator: Page exists - status:', response.status);
        return true;
      } else if (response.status === 404) {
        console.log('🌍 Geolocator: Page not found - status 404');
        return false;
      } else {
        console.log('🌍 Geolocator: Unexpected status:', response.status);
        // For other status codes, assume page doesn't exist
        return false;
      }
    } catch (error) {
      console.warn(
        '🌍 Geolocator: Page existence check failed:',
        error.message
      );
      // If fetch fails (CORS, network error, etc.), assume page doesn't exist
      return false;
    }
  },

  /**
   * Check if current page should preserve its path (collections, products, pages)
   * @returns {boolean} Whether current page should preserve path
   */
  shouldPreservePath() {
    const pathname = window.location.pathname;
    return (
      pathname.includes('/collections/') ||
      pathname.includes('/products/') ||
      pathname.includes('/pages/')
    );
  },

  /**
   * Build target URL with preserved path for collections/products
   * @param {string} baseUrl - Target site base URL
   * @returns {Promise<string>} Final redirect URL
   */
  async buildTargetUrl(baseUrl) {
    // If not a page type we want to preserve path for, return base URL
    if (!this.shouldPreservePath()) {
      return baseUrl;
    }

    try {
      const targetUrl = new URL(baseUrl);
      let currentPath = window.location.pathname;

      // Strip locale prefix: keep only from /collections/, /products/, or /pages/ onwards
      if (currentPath.includes('/collections/')) {
        currentPath = currentPath.substring(
          currentPath.indexOf('/collections/')
        );
      } else if (currentPath.includes('/products/')) {
        currentPath = currentPath.substring(currentPath.indexOf('/products/'));
      } else if (currentPath.includes('/pages/')) {
        currentPath = currentPath.substring(currentPath.indexOf('/pages/'));
      }

      // Build the full target URL with current path
      // IMPORTANT: origin does not include the path (market subfolder), so we must add it back
      const targetPath =
        targetUrl.pathname === '/' ? '' : targetUrl.pathname.replace(/\/$/, '');

      const fullTargetUrl = `${targetUrl.origin}${targetPath}${currentPath}`;

      // Check for in-app browsers (Instagram, Facebook, etc.) which often block/fail the fetch check
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isInApp = /Instagram|FBAN|FBAV/.test(ua);

      if (isInApp) {
        console.log(
          '🌍 Geolocator: In-app browser detected, skipping existence check to ensure deep link'
        );
        return fullTargetUrl;
      }

      // Check if the page exists on the target site
      const pageExists = await this.checkUrlExists(fullTargetUrl);

      if (pageExists) {
        console.log(
          '🌍 Geolocator: Page exists on target site, preserving path'
        );
        return fullTargetUrl;
      } else {
        console.log(
          '🌍 Geolocator: Page does not exist on target site, redirecting to home'
        );
        return baseUrl;
      }
    } catch (error) {
      console.error('Error building target URL:', error);
      return baseUrl;
    }
  },

  /**
   * Redirect to specified URL
   * @param {string} url - Target URL
   * @param {boolean} newTab - Open in new tab
   */
  async redirect(url, newTab = false) {
    try {
      // Build the final target URL (with path preservation if applicable)
      const targetUrl = await this.buildTargetUrl(url);


      if (newTab) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = targetUrl;
      }
    } catch (error) {
      console.error('Redirect failed:', error);
      // Fallback to original URL if something goes wrong
      if (newTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  },

  /**
   * Check if URL is valid
   * @param {string} url - URL to validate
   * @returns {boolean} Whether URL is valid
   */
  isValidUrl(url) {
    try {
      // eslint-disable-next-line no-new
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * Resolve storefront URLs from <link rel="alternate" hreflang> tags (same
 * data Shopify uses for SEO) instead of language root_url in JSON.
 */
const HreflangResolver = {
  getHreflangMap(root = document) {
    const map = new Map();
    const links = root.querySelectorAll('link[rel~="alternate"][hreflang]');
    links.forEach((link) => {
      const hreflang = (link.getAttribute('hreflang') || '').trim();
      if (!hreflang || !link.href) return;
      const key = hreflang.toLowerCase();
      if (!map.has(key)) {
        map.set(key, link.href);
      }
    });
    return map;
  },

  buildCandidates(localeCode, countryCode) {
    const out = [];
    const seen = new Set();
    const add = (tag) => {
      if (tag == null || tag === '') return;
      const k = String(tag).toLowerCase().replace(/_/g, '-');
      if (seen.has(k)) return;
      seen.add(k);
      out.push(k);
    };

    const loc = (localeCode || '').trim();
    const cc = (countryCode || '').trim().toUpperCase();

    if (loc) {
      const norm = loc.replace(/_/g, '-');
      if (norm.includes('-')) {
        const parts = norm.split('-');
        const last = parts[parts.length - 1];
        if (last && last.length === 2) {
          add(
            `${parts[0].toLowerCase()}-${last.toUpperCase()}`
          );
        } else {
          add(norm.toLowerCase());
        }
      }
    }

    const langSub = loc ? loc.split(/[-_]/)[0].toLowerCase() : '';
    if (langSub && cc) {
      add(`${langSub}-${cc}`);
    }
    if (loc && !loc.includes('-') && !loc.includes('_')) {
      add(loc.toLowerCase());
    } else if (langSub) {
      add(langSub);
    }

    return out;
  },

  /**
   * @param {Map<string,string>} hreflangMap
   * @param {string[]} candidates - lowercase hreflang keys
   * @param {string} [marketBaseUrl] - if set, require matching hostname so e.g. generic "en" on .com is not used for a .de market
   * @returns {string|null}
   */
  findHrefForMarket(hreflangMap, candidates, marketBaseUrl) {
    let marketHost = null;
    if (marketBaseUrl && RedirectManager.isValidUrl(marketBaseUrl)) {
      try {
        marketHost = new URL(marketBaseUrl).hostname;
      } catch (e) {
        marketHost = null;
      }
    }

    for (const c of candidates) {
      const href = hreflangMap.get(c);
      if (!href) continue;
      if (marketHost) {
        try {
          if (new URL(href).hostname !== marketHost) continue;
        } catch (e) {
          continue;
        }
      }
      return href;
    }
    return null;
  },
};

/* ==========================================================================
   Geolocator Modal Component
   ========================================================================== */

class GeolocatorModal extends HTMLElement {
  constructor() {
    super();
    this.config = {};
    this.countries = [];
    this.userCountry = null;
    this.isVisible = false;
  }

  /**
   * Initialize the component when connected to DOM
   */
  connectedCallback() {
    this.loadConfig();
    this.loadLocalizationData();
    this.cacheElements();
    this.bindEvents();
    this.loadCountries();
    this.checkShouldShow();
  }

  /**
   * Load configuration from data attributes
   */
  loadConfig() {
    this.config = {
      enabled: true, // Always enabled
      apiEndpoint: 'https://get.geojs.io/v1/ip/geo.json',
      cookieName: 'geolocator_preference',
      cookieDays: 365, // 1 year
      autoRedirect: this.dataset.autoRedirect === 'true',
      modalTitle: this.dataset.modalTitle || 'Choose Your Location',
      modalDescription:
        this.dataset.modalDescription || 'Choose your preferred location',
      closeButtonText: this.dataset.closeButtonText || 'Close',
      showOtherMarkets: this.dataset.showOtherMarkets === 'true',
      visitOtherMarketsText:
        this.dataset.visitOtherMarketsText ||
        'Or visit any of our other markets:',
      dropdownButtonText: this.dataset.dropdownButtonText || 'Select a country',
      searchPlaceholderText:
        this.dataset.searchPlaceholderText || 'Search countries...',
      submitButtonText: this.dataset.submitButtonText || 'Update',
    };
  }

  /**
   * Load localization data from JSON script tag
   */
  loadLocalizationData() {
    const script = this.querySelector('[data-geolocator-localization]');
    if (!script) {
      this.localizationData = {
        countryLanguages: {},
        currentCountry: '',
        currentLocale: '',
      };
      return;
    }
    try {
      this.localizationData = JSON.parse(script.textContent);
    } catch (e) {
      this.localizationData = {
        countryLanguages: {},
        currentCountry: '',
        currentLocale: '',
      };
    }
  }

  /**
   * Cache DOM elements for performance
   */
  cacheElements() {
    this.overlay = this.querySelector('.geolocator-modal__overlay');
    this.container = this.querySelector('.geolocator-modal__container');
    this.closeButtons = this.querySelectorAll('.geolocator-modal__close');
    this.otherCountriesContainer = this.querySelector(
      '.geolocator-modal__other-countries'
    );
    this.template = this.querySelector('.geolocator-modal__country-template');
    this.description = this.querySelector('.geolocator-modal__description');
    this.localizationForm = this.querySelector('.geolocator-form');
    this.languageDropdownContainer = this.querySelector(
      '.geolocator-modal__language-dropdown'
    );
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Close modal events - there are multiple close buttons in the modal
    if (this.closeButtons) {
      this.closeButtons.forEach((closeButton) => {
        closeButton.addEventListener('click', () => this.handleClose());
      });
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.handleClose();
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.handleClose();
      }
    });

    // Form submit for country/language change
    if (this.localizationForm) {
      this.localizationForm.addEventListener('submit', (e) =>
        this.handleFormSubmit(e)
      );
    }

    // Country selection is now handled individually in updateModalContent
  }

  /**
   * Load available countries from DOM
   */
  loadCountries() {
    // Get countries from template instead of direct query
    const countryButtons = this.template.content.querySelectorAll(
      '.geolocator-modal__country-button'
    );
    this.countries = Array.from(countryButtons).map((button) => ({
      code: button.dataset.countryCode,
      name: button
        .querySelector('.geolocator-modal__country-name')
        .textContent.trim(),
      redirectUrl: button.dataset.redirectUrl,
      marketHandle: button.dataset.marketHandle,
      isCurrentSite: button.dataset.isCurrentSite === 'true',
      element: button.cloneNode(true),
    }));

    // Find which country represents the current site
    this.currentSiteCountry = this.countries.find(
      (country) => country.isCurrentSite
    );
  }

  /**
   * Check if modal should be shown
   */
  async checkShouldShow() {
    if (!this.config.enabled) return;

    // Check if user has already made a preference
    const preference = CookieManager.get(this.config.cookieName);
    if (preference) {
      const parsed = this.parsePreference(preference);
      if (parsed.action === 'dismissed') {
        return; // User dismissed, don't show again
      }
      if (parsed.action === 'stayed') {
        return; // User chose to stay, don't show again
      }
      if (parsed.action === 'selected' && this.config.autoRedirect) {
        await this.handleAutoRedirect(parsed);
        return;
      }
    }

    // Get user's current country
    this.userCountry = await GeoLocationAPI.fetchCountry(
      this.config.apiEndpoint
    );

    if (!this.userCountry || !this.userCountry.countryCode) {
      return;
    }

    // Check if current site matches user's country
    const shouldShow = this.shouldShowForCountry(this.userCountry.countryCode);

    if (shouldShow) {
      this.updateModalContent();
      this.show();
    }
  }

  /**
   * Parse stored preference cookie
   * @param {string} preference - Cookie value
   * @returns {Object} Parsed preference
   */
  parsePreference(preference) {
    try {
      return JSON.parse(preference);
    } catch {
      return { action: 'unknown' };
    }
  }

  /**
   * Handle auto-redirect based on stored preference
   * @param {Object} preference - User preference
   */
  async handleAutoRedirect(preference) {
    if (
      preference.redirectUrl &&
      RedirectManager.isValidUrl(preference.redirectUrl)
    ) {
      // Check if we're not already on the preferred site
      const preferredUrl = new URL(preference.redirectUrl);
      if (!window.location.href.includes(preferredUrl.hostname)) {
        await RedirectManager.redirect(preference.redirectUrl);
      }
    }
  }

  /**
   * Determine if modal should show for detected country
   * @param {string} countryCode - Detected country code
   * @returns {boolean} Whether to show modal
   */
  shouldShowForCountry(countryCode) {
    // Check if countryCode is valid
    if (!countryCode || typeof countryCode !== 'string') {
      return false;
    }

    // Check if we have a current site country configured
    if (!this.currentSiteCountry) {
      return false;
    }

    // If user's country matches the current site, don't show modal
    if (countryCode.toUpperCase() === this.currentSiteCountry.code) {
      return false;
    }

    // Find if we have a different market for this user's country
    let matchingCountry = this.countries.find(
      (country) => country.code === countryCode.toUpperCase()
    );

    // If no country match found, try continent code
    if (!matchingCountry && this.userCountry?.continentCode) {
      matchingCountry = this.countries.find(
        (country) =>
          country.code === this.userCountry.continentCode.toUpperCase()
      );
    }

    // Ultimate fallback: look for INT (international) country code
    if (!matchingCountry) {
      matchingCountry = this.countries.find(
        (country) => country.code === 'INT'
      );
    }

    // Suppress modal if the recommended market equals the current site or same hostname
    try {
      const isSameMarket =
        matchingCountry &&
        (matchingCountry.isCurrentSite ||
          (this.currentSiteCountry &&
            matchingCountry.code === this.currentSiteCountry.code));

      let isSameHostname = false;
      if (matchingCountry && matchingCountry.redirectUrl) {
        const targetHostname = new URL(
          matchingCountry.redirectUrl
        ).hostname.replace(/^www\./, '');
        const currentHostname = window.location.hostname.replace(/^www\./, '');
        isSameHostname = targetHostname === currentHostname;
      }

      if (isSameMarket || isSameHostname) {
        return false;
      }
    } catch (error) {
      // Ignore URL parsing issues
    }

    if (matchingCountry && matchingCountry.redirectUrl) {
      return true;
    }

    return false;
  }

  /**
   * Update modal content with user's country and reorder countries
   */
  updateModalContent() {
    // Update description with user's country name
    if (this.description && this.userCountry) {
      const descriptionText = this.config.modalDescription.replace(
        '[country_name]',
        this.userCountry.country
      );
      this.description.textContent = descriptionText;
    }


    // Add all countries as a custom dropdown (only once - reuse on subsequent opens)
    if (this.config.showOtherMarkets) {
      const existingDropdown = this.otherCountriesContainer.querySelector(
        '.geolocator-dropdown:not(.geolocator-dropdown--language)'
      );
      if (!existingDropdown) {

        const allCountries = this.countries.slice();
        if (allCountries.length > 0) {
          this.createCustomDropdown(allCountries);
        }
      }
    }
  }

  /**
   * Create a custom dropdown for other markets
   * @param {Array} countries - Array of country objects to include in dropdown
   */
  createCustomDropdown(countries) {
    // Sort countries alphabetically by name
    const sortedCountries = countries.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Create dropdown container
    const dropdownContainer = document.createElement('div');
    dropdownContainer.className = 'geolocator-dropdown';

    // Create searchable button/input hybrid
    const dropdownWrapper = document.createElement('div');
    dropdownWrapper.className = 'geolocator-dropdown__wrapper';

    // Create hidden input for search functionality
    const searchInputContainer = document.createElement('div');
    searchInputContainer.className = 'geolocator-dropdown__search-container';
    searchInputContainer.style.display = 'none';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.id = 'geolocator-search-input';
    searchInput.name = 'geolocator-search';
    searchInput.className = 'geolocator-dropdown__search-input';
    searchInput.placeholder = this.config.searchPlaceholderText;
    searchInput.setAttribute('aria-label', this.config.searchPlaceholderText);
    searchInput.setAttribute('autocomplete', 'country-name');

    const searchArrow = document.createElement('div');
    searchArrow.className = 'geolocator-dropdown__search-arrow';
    searchArrow.innerHTML = `
      <svg class="geolocator-dropdown__arrow geolocator-dropdown__arrow--open" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `;

    searchInputContainer.appendChild(searchInput);
    searchInputContainer.appendChild(searchArrow);

    // Pre-select: userCountry (geo-detected) if it matches, else current site
    const userCountryCode =
      this.userCountry?.countryCode?.toUpperCase() || '';
    const currentSiteCode =
      this.currentSiteCountry?.code ||
      (this.localizationData?.currentCountry || '').toUpperCase();
    const defaultCountry =
      sortedCountries.find((c) => c.code === userCountryCode) ||
      sortedCountries.find((c) => c.code === currentSiteCode) ||
      this.currentSiteCountry ||
      sortedCountries[0];

    const defaultFlagFallback = '🌍';

    const getDefaultButtonContent = () => {
      if (!defaultCountry) {
        return {
          flagHtml: defaultFlagFallback,
          displayName: this.config.dropdownButtonText,
        };
      }
      const flagEl = defaultCountry.element?.querySelector(
        '.geolocator-modal__country-flag'
      );
      let flagHtml = defaultFlagFallback;
      if (flagEl) {
        const img = flagEl.querySelector('.geolocator-modal__flag-image');
        const fallbackEl = flagEl.querySelector('.geolocator-modal__flag-fallback');
        if (img && img.src) {
          flagHtml = `<img src="${img.src}" alt="${defaultCountry.name} flag" class="geolocator-dropdown__flag-image" width="20" height="15" loading="lazy">`;
        } else if (fallbackEl) {
          flagHtml = defaultFlagFallback;
        }
      }
      let displayName = defaultCountry.name;
      return { flagHtml, displayName };
    };

    const { flagHtml, displayName } = getDefaultButtonContent();

    // Create dropdown button that transforms into input
    const dropdownButton = document.createElement('button');
    dropdownButton.type = 'button';
    dropdownButton.className = 'geolocator-dropdown__button';
    dropdownButton.setAttribute('aria-expanded', 'false');
    dropdownButton.innerHTML = `
      <span class="geolocator-dropdown__flag">${flagHtml}</span>
      <span class="geolocator-dropdown__label">${displayName}</span>
      <svg class="geolocator-dropdown__arrow" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `;

    // Create dropdown list
    const dropdownList = document.createElement('div');
    dropdownList.className = 'geolocator-dropdown__list';
    dropdownList.setAttribute('role', 'listbox');
    dropdownList.style.display = 'none';

    // Store reference to this instance's dropdown list for cleanup
    if (!this.dropdownLists) {
      this.dropdownLists = [];
    }
    this.dropdownLists.push(dropdownList);

    // Populate dropdown options
    const populateOptions = (countriesToShow = sortedCountries) => {
      dropdownList.innerHTML = '';

      if (countriesToShow.length === 0) {
        const noResults = document.createElement('div');
        noResults.className = 'geolocator-dropdown__no-results';
        noResults.textContent = window.theme.strings.geolocator.noResults;
        dropdownList.appendChild(noResults);
        return;
      }

      countriesToShow.forEach((country) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'geolocator-dropdown__option';
        option.setAttribute('role', 'option');
        option.dataset.countryCode = country.code;
        option.dataset.redirectUrl = country.redirectUrl;
        option.dataset.marketHandle = country.marketHandle;
        option.dataset.isCurrentSite = country.isCurrentSite;

        // Get flag content from the original button element (custom image or fallback)
        const flagElement = country.element.querySelector(
          '.geolocator-modal__country-flag'
        );

        const defaultFlagFallback = '🌍';
        let flagContent = defaultFlagFallback;

        if (flagElement) {
          const flagImage = flagElement.querySelector(
            '.geolocator-modal__flag-image'
          );
          const fallbackEl = flagElement.querySelector(
            '.geolocator-modal__flag-fallback'
          );

          if (flagImage && flagImage.src) {
            flagContent = `<img src="${flagImage.src}" alt="${country.name} flag" class="geolocator-dropdown__flag-image" width="20" height="15" loading="lazy">`;
          } else if (fallbackEl) {
            flagContent = defaultFlagFallback;
          }
        }

        let displayName = country.name;
        let extraClass = '';

        const isSelected = defaultCountry && country.code === defaultCountry.code;
        if (isSelected) {
          option.setAttribute('aria-selected', 'true');
          extraClass += ' geolocator-dropdown__option--selected';
        }

        option.className += extraClass;

        option.innerHTML = `
          <span class="geolocator-dropdown__flag">${flagContent}</span>
          <span class="geolocator-dropdown__name">${displayName}</span>
        `;

        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleDropdownSelection(option);
        });

        dropdownList.appendChild(option);
      });
    };

    // Initial population
    populateOptions();

    // Search functionality - reactive to input changes
    const handleSearch = (searchTerm) => {
      if (searchTerm === '') {
        populateOptions();
      } else {
        const filteredCountries = sortedCountries.filter(
          (country) =>
            country.name.toLowerCase().includes(searchTerm) ||
            country.code.toLowerCase().includes(searchTerm)
        );
        populateOptions(filteredCountries);
      }
    };

    searchInput.addEventListener('input', (e) => {
      const searchTerm = e.target.value.toLowerCase().trim();
      handleSearch(searchTerm);
    });

    // Position dropdown list relative to button
    const positionDropdownList = () => {
      // Get the position of the visible element (either button or search container)
      const visibleElement =
        searchInputContainer.style.display === 'none'
          ? dropdownButton
          : searchInputContainer;
      const rect = visibleElement.getBoundingClientRect();

      const topPosition = rect.bottom + window.scrollY + 4;
      const leftPosition = rect.left + window.scrollX;

      // Position dropdown below the visible element

      dropdownList.style.top = `${topPosition}px`;
      dropdownList.style.left = `${leftPosition}px`;
      dropdownList.style.width = `${rect.width}px`;
    };

    // Toggle between button and input modes
    const activateSearchMode = () => {
      dropdownButton.style.display = 'none';
      searchInputContainer.style.display = 'flex';

      // Small delay to ensure the input is rendered before positioning
      setTimeout(() => {
        searchInput.focus();
        positionDropdownList();
        dropdownList.style.display = 'block';
      }, 10);

      dropdownContainer.classList.add('geolocator-dropdown--active');

      // Prevent modal scrolling
      const modalContainer = this.container;
      if (modalContainer) {
        modalContainer.classList.add('geolocator-dropdown--active-modal');
      }
    };

    const deactivateSearchMode = () => {
      searchInputContainer.style.display = 'none';
      dropdownButton.style.display = 'flex';
      dropdownList.style.display = 'none';
      searchInput.value = '';
      populateOptions(); // Reset to all countries
      dropdownContainer.classList.remove('geolocator-dropdown--active');
      dropdownButton.setAttribute('aria-expanded', 'false');
      dropdownButton.classList.remove('geolocator-dropdown__button--open');

      // Restore modal scrolling
      const modalContainer = this.container;
      if (modalContainer) {
        modalContainer.classList.remove('geolocator-dropdown--active-modal');
      }
    };

    // Button click activates search mode
    dropdownButton.addEventListener('click', (e) => {
      e.preventDefault();
      activateSearchMode();
      dropdownButton.setAttribute('aria-expanded', 'true');
      dropdownButton.classList.add('geolocator-dropdown__button--open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (
        !dropdownContainer.contains(e.target) &&
        !dropdownList.contains(e.target)
      ) {
        deactivateSearchMode();
      }
    });

    // Keyboard navigation
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        deactivateSearchMode();
        dropdownButton.focus();
      }
      // Allow Tab to close dropdown and move focus
      if (e.key === 'Tab') {
        deactivateSearchMode();
      }
    });

    // Handle input blur (when user clicks somewhere else or tabs out)
    searchInput.addEventListener('blur', (e) => {
      // Small delay to allow clicks on dropdown options to register
      setTimeout(() => {
        if (!dropdownContainer.contains(document.activeElement)) {
          deactivateSearchMode();
        }
      }, 150);
    });

    // Reposition dropdown on scroll/resize
    const repositionHandler = () => {
      if (dropdownList.style.display === 'block') {
        positionDropdownList();
      }
    };

    window.addEventListener('scroll', repositionHandler);
    window.addEventListener('resize', repositionHandler);

    // Assemble dropdown
    dropdownWrapper.appendChild(searchInputContainer);
    dropdownWrapper.appendChild(dropdownButton);
    dropdownContainer.appendChild(dropdownWrapper);

    // Append dropdown list to body to avoid modal overflow constraints
    document.body.appendChild(dropdownList);

    // Add to modal
    this.otherCountriesContainer.appendChild(dropdownContainer);

    // Create language dropdown and set initial form state
    if (this.languageDropdownContainer && this.localizationForm) {
      this.selectedCountryCode = defaultCountry?.code || '';
      this.selectedCountryData = defaultCountry || null;
      this.createLanguageDropdown(defaultCountry?.code || '');
    }
  }

  /**
   * Create language dropdown and populate based on selected country
   * @param {string} countryCode - Country code to look up languages for
   */
  createLanguageDropdown(countryCode) {
    // Clean up previous language dropdown list from body
    if (this._languageDropdownList) {
      this._languageDropdownList.remove();
      this._languageDropdownList = null;
    }
    this.languageDropdownContainer.innerHTML = '';

    const data = this.localizationData || {};
    const countryLanguages = data.countryLanguages || {};
    const languages = countryLanguages[countryCode] || [];
    const currentLocale = data.currentLocale || '';

    if (languages.length <= 1) {
      const locale =
        languages.length === 1 ? languages[0].iso_code : currentLocale;
      this.updateHiddenInputs(countryCode, locale || '');
      return;
    }

    const selectedLang =
      languages.find((l) => l.iso_code === currentLocale) || languages[0];
    this._selectedLanguage = selectedLang;

    const dropdownContainer = document.createElement('div');
    dropdownContainer.className = 'geolocator-dropdown geolocator-dropdown--language';

    const dropdownWrapper = document.createElement('div');
    dropdownWrapper.className = 'geolocator-dropdown__wrapper';

    const dropdownButton = document.createElement('button');
    dropdownButton.type = 'button';
    dropdownButton.className = 'geolocator-dropdown__button';
    dropdownButton.setAttribute('aria-expanded', 'false');
    dropdownButton.innerHTML = `
      <span class="geolocator-dropdown__label">${selectedLang.endonym_name || selectedLang.iso_code}</span>
      <svg class="geolocator-dropdown__arrow" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `;

    const dropdownList = document.createElement('div');
    dropdownList.className = 'geolocator-dropdown__list';
    dropdownList.setAttribute('role', 'listbox');
    dropdownList.style.display = 'none';
    this._languageDropdownList = dropdownList;

    if (this.dropdownLists) {
      this.dropdownLists.push(dropdownList);
    }

    languages.forEach((lang) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'geolocator-dropdown__option';
      option.setAttribute('role', 'option');
      option.dataset.locale = lang.iso_code;

      if (lang.iso_code === selectedLang.iso_code) {
        option.setAttribute('aria-selected', 'true');
        option.className += ' geolocator-dropdown__option--selected';
      }

      option.innerHTML = `
        <span class="geolocator-dropdown__name">${lang.endonym_name || lang.iso_code}</span>
      `;

      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        this._selectedLanguage = lang;
        const label = dropdownButton.querySelector('.geolocator-dropdown__label');
        if (label) label.textContent = lang.endonym_name || lang.iso_code;

        dropdownList.querySelectorAll('.geolocator-dropdown__option').forEach((opt) => {
          opt.classList.remove('geolocator-dropdown__option--selected');
          opt.removeAttribute('aria-selected');
        });
        option.classList.add('geolocator-dropdown__option--selected');
        option.setAttribute('aria-selected', 'true');

        dropdownList.style.display = 'none';
        dropdownButton.setAttribute('aria-expanded', 'false');
        dropdownButton.classList.remove('geolocator-dropdown__button--open');
        dropdownContainer.classList.remove('geolocator-dropdown--active');

        const modalContainer = this.container;
        if (modalContainer) {
          modalContainer.classList.remove('geolocator-dropdown--active-modal');
        }

        const code =
          this.selectedCountryCode ||
          (this.localizationData?.currentCountry || '').toUpperCase();
        this.updateHiddenInputs(code, lang.iso_code);
      });

      dropdownList.appendChild(option);
    });

    const positionDropdownList = () => {
      const rect = dropdownButton.getBoundingClientRect();
      dropdownList.style.top = `${rect.bottom + window.scrollY + 4}px`;
      dropdownList.style.left = `${rect.left + window.scrollX}px`;
      dropdownList.style.width = `${rect.width}px`;
    };

    dropdownButton.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = dropdownList.style.display === 'block';
      if (isOpen) {
        dropdownList.style.display = 'none';
        dropdownButton.setAttribute('aria-expanded', 'false');
        dropdownButton.classList.remove('geolocator-dropdown__button--open');
        dropdownContainer.classList.remove('geolocator-dropdown--active');
        if (this.container) {
          this.container.classList.remove('geolocator-dropdown--active-modal');
        }
      } else {
        positionDropdownList();
        dropdownList.style.display = 'block';
        dropdownButton.setAttribute('aria-expanded', 'true');
        dropdownButton.classList.add('geolocator-dropdown__button--open');
        dropdownContainer.classList.add('geolocator-dropdown--active');
        if (this.container) {
          this.container.classList.add('geolocator-dropdown--active-modal');
        }
      }
    });

    document.addEventListener('click', (e) => {
      if (
        !dropdownContainer.contains(e.target) &&
        !dropdownList.contains(e.target)
      ) {
        dropdownList.style.display = 'none';
        dropdownButton.setAttribute('aria-expanded', 'false');
        dropdownButton.classList.remove('geolocator-dropdown__button--open');
        dropdownContainer.classList.remove('geolocator-dropdown--active');
        if (this.container) {
          this.container.classList.remove('geolocator-dropdown--active-modal');
        }
      }
    });

    const repositionHandler = () => {
      if (dropdownList.style.display === 'block') {
        positionDropdownList();
      }
    };
    window.addEventListener('scroll', repositionHandler);
    window.addEventListener('resize', repositionHandler);

    dropdownWrapper.appendChild(dropdownButton);
    dropdownContainer.appendChild(dropdownWrapper);
    document.body.appendChild(dropdownList);
    this.languageDropdownContainer.appendChild(dropdownContainer);
    this.updateHiddenInputs(countryCode, selectedLang.iso_code);
  }

  /**
   * Update hidden form inputs for country and locale
   */
  updateHiddenInputs(countryCode, localeCode) {
    const form = this.localizationForm;
    if (!form) return;
    const countryInput = form.querySelector(
      'input[name="country_code"], #geolocator-country-code'
    );
    const localeInput = form.querySelector(
      'input[name="locale_code"], #geolocator-locale-code'
    );
    if (countryInput) countryInput.value = countryCode || '';
    if (localeInput) localeInput.value = localeCode || '';
  }

  /**
   * Handle dropdown option selection
   * @param {HTMLElement} option - Selected dropdown option
   */
  handleDropdownSelection(option) {
    const countryData = {
      code: option.dataset.countryCode,
      redirectUrl: option.dataset.redirectUrl,
      marketHandle: option.dataset.marketHandle,
      isCurrentSite: option.dataset.isCurrentSite === 'true',
    };
    const countryName =
      option.querySelector('.geolocator-dropdown__name')?.textContent ||
      this.countries.find((c) => c.code === countryData.code)?.name ||
      countryData.code;

    // Find the associated dropdown elements
    const dropdownContainer = this.otherCountriesContainer.querySelector(
      '.geolocator-dropdown:not(.geolocator-dropdown--language)'
    );
    if (!dropdownContainer) return;
    const searchInputContainer = dropdownContainer.querySelector(
      '.geolocator-dropdown__search-container'
    );
    const searchInput = dropdownContainer.querySelector(
      '.geolocator-dropdown__search-input'
    );
    const dropdownButton = dropdownContainer.querySelector(
      '.geolocator-dropdown__button'
    );
    const dropdownList = option.closest('.geolocator-dropdown__list');

    // Reset to button mode (hide search container, show button)
    if (searchInputContainer) searchInputContainer.style.display = 'none';
    if (dropdownButton) dropdownButton.style.display = 'flex';
    if (dropdownList) dropdownList.style.display = 'none';
    if (searchInput) searchInput.value = '';
    dropdownContainer.classList.remove('geolocator-dropdown--active');
    if (dropdownButton)
      dropdownButton.setAttribute('aria-expanded', 'false');
    if (dropdownButton)
      dropdownButton.classList.remove('geolocator-dropdown__button--open');

    // Restore modal scrolling
    const modalContainer = this.container;
    if (modalContainer) {
      modalContainer.classList.remove('geolocator-dropdown--active-modal');
    }

    // When form exists: update form state, don't redirect (user clicks Submit)
    if (this.localizationForm) {
      this.selectedCountryCode = countryData.code;
      this.selectedCountryData = countryData;
      if (dropdownButton) {
        const optionFlag = option.querySelector('.geolocator-dropdown__flag');
        const buttonFlag = dropdownButton.querySelector(
          '.geolocator-dropdown__flag'
        );
        if (optionFlag && buttonFlag) {
          buttonFlag.innerHTML = optionFlag.innerHTML;
        }
        const label = dropdownButton.querySelector(
          '.geolocator-dropdown__label'
        );
        if (label) label.textContent = countryName;
      }
      this.updateHiddenInputs(countryData.code, '');
      this.createLanguageDropdown(countryData.code);
      return;
    }

    // Legacy: no form - handle current site selection as "stay" action
    if (countryData.isCurrentSite) {
      this.savePreference('stayed');
      this.hide();
      return;
    }

    // Legacy: Handle regular country selection with immediate redirect
    this.savePreference('selected', countryData);

    if (
      countryData.redirectUrl &&
      RedirectManager.isValidUrl(countryData.redirectUrl)
    ) {
      RedirectManager.redirect(countryData.redirectUrl);
    }

    this.hide();
  }

  /**
   * Handle form submit for country/language change
   * @param {Event} e - Submit event
   */
  handleFormSubmit(e) {
    e.preventDefault();
    const countryInput = this.localizationForm?.querySelector(
      'input[name="country_code"], #geolocator-country-code'
    );
    const localeInput = this.localizationForm?.querySelector(
      'input[name="locale_code"], #geolocator-locale-code'
    );
    const countryCode = countryInput?.value?.toUpperCase() || '';
    const localeCode = localeInput?.value || '';

    const countryData = this.selectedCountryData || this.countries.find(
      (c) => c.code === countryCode
    );

    // Current site: submit localization form (Shopify handles redirect)
    if (
      countryData?.isCurrentSite ||
      (this.currentSiteCountry && countryCode === this.currentSiteCountry.code)
    ) {
      this.localizationForm.submit();
      return;
    }

    // Different market: resolve URL from hreflang alternates (head links)
    if (
      countryData?.redirectUrl &&
      RedirectManager.isValidUrl(countryData.redirectUrl)
    ) {
      const hreflangMap = HreflangResolver.getHreflangMap(document);
      const candidates = HreflangResolver.buildCandidates(
        localeCode,
        countryCode
      );
      const fromAlternates = HreflangResolver.findHrefForMarket(
        hreflangMap,
        candidates,
        countryData.redirectUrl
      );
      const base = countryData.redirectUrl.replace(/\/$/, '');
      const url = fromAlternates || base;
      this.savePreference('selected', {
        ...countryData,
        redirectUrl: url,
      });
      RedirectManager.redirect(url);
    }

    this.hide();
  }

  /**
   * Show the modal
   */
  show() {
    // Always update modal content when showing
    if (this.userCountry) {
      this.updateModalContent();
    }
    this.isVisible = true;
    ModalManager.show(this);
  }

  /**
   * Hide the modal
   */
  hide() {
    this.isVisible = false;

    // Hide dropdown lists but keep them in DOM so they work when modal reopens
    if (this.dropdownLists) {
      this.dropdownLists.forEach((list) => {
        list.style.display = 'none';
      });
    }

    ModalManager.hide(this);
  }

  /**
   * Handle close button click
   */
  handleClose() {
    this.savePreference('dismissed');
    this.hide();
  }

  /**
   * Save user preference to cookie
   * @param {string} action - User action (dismissed, stayed, selected)
   * @param {Object} data - Additional data for the preference
   */
  savePreference(action, data = {}) {
    const preference = {
      action,
      timestamp: Date.now(),
      userCountry: this.userCountry?.countryCode,
      ...data,
    };

    CookieManager.set(
      this.config.cookieName,
      JSON.stringify(preference),
      this.config.cookieDays
    );
  }
}

/* ==========================================================================
   Geolocator Button Component
   ========================================================================== */

class GeolocatorButton extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Initialize the component when connected to DOM
   */
  connectedCallback() {
    this.bindEvents();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const button = this.querySelector('button');

    if (button) {
      button.addEventListener('click', () => this.handleClick());
    }
  }

  /**
   * Handle button click to open modal
   */
  async handleClick() {
    const modal = document.querySelector('geolocator-modal');

    if (modal) {
      // Ensure we have user country data before showing
      if (!modal.userCountry) {
        modal.userCountry = await GeoLocationAPI.fetchCountry(
          modal.config.apiEndpoint
        );
      }
      modal.show();
    }
  }
}

/* ==========================================================================
   Component Registration & Public API
   ========================================================================== */

// Register custom elements
try {
  if (!customElements.get('geolocator-modal')) {
    customElements.define('geolocator-modal', GeolocatorModal);
  }

  if (!customElements.get('geolocator-button')) {
    customElements.define('geolocator-button', GeolocatorButton);
  }
} catch (error) {
  // Elements may already be defined, ignore the error
  console.warn('Geolocator custom elements already defined:', error.message);
}

// Expose utilities for external use
window.GeolocatorAPI = {
  CookieManager,
  GeoLocationAPI,
  ModalManager,
  RedirectManager,

  /**
   * Manually trigger the geolocator modal
   */
  showModal() {
    const modal = document.querySelector('geolocator-modal');
    if (modal) {
      modal.show();
    }
  },

  /**
   * Hide the geolocator modal
   */
  hideModal() {
    const modal = document.querySelector('geolocator-modal');
    if (modal) {
      modal.hide();
    }
  },

  /**
   * Clear user preferences
   */
  clearPreferences() {
    const modal = document.querySelector('geolocator-modal');
    if (modal) {
      CookieManager.delete(modal.config.cookieName);
    }
  },

  /**
   * Get current user preferences
   * @returns {Object|null} User preferences
   */
  getPreferences() {
    const modal = document.querySelector('geolocator-modal');
    if (modal) {
      const preference = CookieManager.get(modal.config.cookieName);
      return preference ? JSON.parse(preference) : null;
    }
    return null;
  },
};

// Development mode debug utilities
if (
  window.location.hostname === 'localhost' ||
  window.location.hostname.includes('ngrok')
) {
  // Add debug methods
  window.GeolocatorAPI.debug = {
    testCountry(countryCode) {
      const modal = document.querySelector('geolocator-modal');
      if (modal) {
        modal.userCountry = { countryCode, country: countryCode };
        modal.show();
      }
    },

    clearCookies() {
      const modal = document.querySelector('geolocator-modal');
      if (modal) {
        CookieManager.delete(modal.config.cookieName);
      }
    },
  };
}